import { useCallback } from 'react';
import { useAjaxCall } from '~/_shared/utils/hooks/useAjaxCall';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { type MetricValue } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import {
  createWMSOptimizedTerritories, type CreateWMSTerritoriesRequestParams, type TargetMetric, type WMSMetricConstraint,
} from '~/store/boundaries/boundaries.repository';
import {
  isLocationCount as isTotalLocationCountMetric, normalizeEmptyGroupIdForBackend,
} from './modalSteps/constraintsValidation/constraints.helpers';
import { LOCATION_COUNT_ID } from './modalSteps/constraintsValidation/constraints.repository';

export const useGenerateWMSOptimizedTerritories = () => {
  const { invokeAjax, isLoading } = useAjaxCall((clientId: number, params: CreateWMSTerritoriesRequestParams) =>
    createWMSOptimizedTerritories(clientId, params)
  );

  const createOptimizedTerritoriesRequest = useCallback(({
    clientId,
    dataMetricsValues,
    demographicsValues,
    useConstraints,
    targetTerritoryCount,
    ...restData
  }: {
    clientId: number;
    dataMetricsValues: readonly MetricValue[];
    demographicsValues: readonly MetricValue[];
    useConstraints: boolean;
    targetTerritoryCount?: number;
  } & Omit<CreateWMSTerritoriesRequestParams, 'targetMetrics' | 'constraints'>
  ) => {
    const metrics = [
      ...dataMetricsValues.filter(dataMetricsValue => dataMetricsValue.checked),
      ...demographicsValues.filter(demographicsValue => demographicsValue.checked),
    ];

    const targetMetrics: TargetMetric[] = metrics.map(metricValue => {
      const metric = metricForRequest(metricValue);

      return ({
        name: metric.metric,
        column_id: metric.column_id,
        column_value: metric.column_value,
        weighting: metricValue.weight,
      });
    });

    const constraints = useConstraints
      ? metrics
        .filter(metric => metric.isConstraint)
        .flatMap(metric => [
          {
            ...metricForRequest(metric),
            operator: 'gte',
            value: metric.constraintLowerBound,
          }, {
            ...metricForRequest(metric),
            operator: 'lte',
            value: metric.constraintUpperBound,
          },
        ])
        .filter((constraint): constraint is WMSMetricConstraint => notNullsy(constraint.value))
      : undefined;

    return invokeAjax(clientId, {
      ...restData,
      // default to 50 territories if no constraints and number of territories not specified
      targetTerritoryCount: targetTerritoryCount ?? (constraints?.length ? undefined : 50),
      targetMetrics,
      constraints,
    });
  }, [invokeAjax]);

  return {
    isLoading,
    createOptimizedTerritoriesRequest,
  };
};

const metricForRequest = (metric: MetricValue): Pick<WMSMetricConstraint, 'metric' | 'column_id' | 'column_value'> => {
  const isTotalLocationCount = isTotalLocationCountMetric(metric.id);

  // Total location count metric
  if (isTotalLocationCount) {
    return {
      column_id: null,
      column_value: null,
      metric: LOCATION_COUNT_ID,
    };
  }

  // Group/categorical metric
  if (metric.groupId) {
    return {
      column_id: metric.id,
      column_value: normalizeEmptyGroupIdForBackend(metric.groupId),
      metric: LOCATION_COUNT_ID,
    };
  }

  // Regular numeric column metric
  return {
    metric: metric.id,
  };
};
